/**
 * TaskCancelModal — Görev iptal senaryosu seçimi.
 *
 * 2 politika:
 *   FULL_POINTS    → Herkese Tam Puan Ver
 *   CANCEL_REVOKE  → Görevi İptal Et, Puanları Geri Al
 *
 * NOT: Veri düzeltmesi / yeniden hesaplama için ayrı "Yeniden Hesapla"
 * butonu kullanılır (Tasks/index.jsx). REPLACE_RECALC bu modaldan kaldırıldı.
 */
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { cancelTask } from '../../store/slices/tasksSlice';
import { pushToast } from '../../store/slices/uiSlice';
import Modal from '../../components/ui/Modal';
import Button from '../../components/ui/Button';
import Icon from '../../components/ui/Icon';

const POLICIES = [
  {
    value: 'FULL_POINTS',
    icon: 'check-circle',
    iconColor: '#22c55e',
    borderActive: 'border-green-500',
    bgActive: 'bg-green-50',
    badgeColor: 'bg-green-100 text-green-800',
    label: 'Herkese Tam Puan Ver',
    subtitle: 'Toplu Telafi Kararı',
    description:
      `Sistemsel hata, veri eksikliği veya eşitsizlik durumunda tüm katılımcılara maksimum puan verilir. Leaderboard yeniden dengelenir.`,
    scenario:
      `Örn: Kasko üretimi görevinde entegrasyon hatası nedeniyle bazı kullanıcıların üretimleri sayılmadı. Tüm katılımcılara 10 puan (max) yüklenir.`,
    userMessage:
      `"Teknik bir aksaklık nedeniyle bu görev tüm katılımcılar için tam puanla sonuçlandırılmıştır."`,
  },
  {
    value: 'CANCEL_REVOKE',
    icon: 'x-circle',
    iconColor: '#ef4444',
    borderActive: 'border-red-500',
    bgActive: 'bg-red-50',
    badgeColor: 'bg-red-100 text-red-800',
    label: 'Görevi İptal Et, Puanları Geri Al',
    subtitle: 'Kural Hatası / Suistimal',
    description:
      `Görev tamamen hatalı kurgulanmışsa veya suistimal tespit edildiyse kullanılır. Verilen tüm puanlar geri alınır, kullanıcı skorları eski haline döner.`,
    scenario:
      `Örn: "DASK üretimi %50 geçerse bonus" görevi yanlış tanımlandı, şartı sağlamayanlar bile +5 puan aldı. Görev iptal edilerek tüm puanlar geri çekilir.`,
    userMessage:
      `"Görev kurgusunda tespit edilen hata nedeniyle ilgili görev iptal edilmiş ve puanlar geri alınmıştır."`,
  },
];

export default function TaskCancelModal({ task, onClose, onSuccess }) {
  const dispatch = useDispatch();

  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [loading, setLoading] = useState(false);

  if (!task) return null;

  const canConfirm = selectedPolicy !== null;

  const handleConfirm = async () => {
    if (!canConfirm) return;
    setLoading(true);
    try {
      const payload = { policy: selectedPolicy };
      await dispatch(cancelTask({ id: task.id, payload })).unwrap();
      dispatch(pushToast({ type: 'success', message: `"${task.name}" görevi iptal edildi.` }));
      onSuccess?.();
      onClose();
    } catch (err) {
      dispatch(pushToast({ type: 'error', message: err || 'İptal işlemi başarısız.' }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Görevi İptal Et"
      size="lg"
    >
      <div className="space-y-4">
        {/* Görev bilgisi */}
        <div className="flex items-start gap-3 rounded-xl border border-neutral-200 bg-neutral-50 px-4 py-3">
          <Icon name="alert-triangle" className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-neutral-800">{task.name}</p>
            <p className="text-[12px] text-neutral-500 mt-0.5">
              Bu işlem geri alınamaz. İptal politikasını dikkatlice seçin.
            </p>
          </div>
        </div>

        {/* Politika kartları */}
        <div className="space-y-2">
          <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide">İptal Senaryosu Seçin</p>
          {POLICIES.map((p) => {
            const isActive = selectedPolicy === p.value;
            return (
              <button
                key={p.value}
                type="button"
                onClick={() => setSelectedPolicy(p.value)}
                className={[
                  'w-full text-left rounded-xl border-2 px-4 py-3 transition-all',
                  isActive
                    ? `${p.borderActive} ${p.bgActive}`
                    : 'border-neutral-200 bg-white hover:border-neutral-300 hover:bg-neutral-50',
                ].join(' ')}
              >
                <div className="flex items-start gap-3">
                  {/* Radio indicator */}
                  <div className={[
                    'mt-0.5 h-4 w-4 flex-shrink-0 rounded-full border-2 flex items-center justify-center',
                    isActive ? `${p.borderActive}` : 'border-neutral-300',
                  ].join(' ')}>
                    {isActive && <div className="h-2 w-2 rounded-full" style={{ backgroundColor: p.iconColor }} />}
                  </div>

                  <div className="flex-1 min-w-0">
                    {/* Başlık + rozet */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-neutral-900">{p.label}</span>
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${p.badgeColor}`}>
                        {p.subtitle}
                      </span>
                    </div>

                    {/* Açıklama */}
                    <p className="text-[12px] text-neutral-600 mt-1 leading-relaxed">
                      {p.description}
                    </p>

                    {/* Senaryo */}
                    {isActive && (
                      <div className="mt-2 rounded-lg bg-white/80 border border-current/10 px-3 py-2 space-y-1.5"
                        style={{ borderColor: p.iconColor + '33' }}>
                        <p className="text-[11px] text-neutral-500 font-medium">📋 Örnek Senaryo</p>
                        <p className="text-[11px] text-neutral-600 leading-relaxed">{p.scenario}</p>
                        <p className="text-[11px] text-neutral-500 font-medium mt-1">💬 Kullanıcıya Mesaj</p>
                        <p className="text-[11px] text-neutral-600 italic">{p.userMessage}</p>
                      </div>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Alt butonlar */}
        <div className="flex justify-end gap-3 pt-2 border-t border-neutral-100">
          <Button variant="ghost" onClick={onClose} disabled={loading}>
            Vazgeç
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirm}
            disabled={!canConfirm || loading}
            loading={loading}
            icon="x-circle"
          >
            İptal Et
          </Button>
        </div>
      </div>
    </Modal>
  );
}
